import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { message, Row, Flex, Tooltip, Tag } from 'antd';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import {
  CustomButton,
  CustomShipmentText,
  CustomTabs,
} from '../../../components';
import {
  DeleteShipmentGeoFence,
  deleteShipmentNotification,
  muteShipmentNotificationsYourself,
  shipmentNotificationListing,
} from '../../../apis/shipment';
import NotificationListSection from './components/NotificationListSection/NotificationListSection';
import PerShipmentNotificationModal from '../PerShipmentNotification/PerShipmentNotificationModal';
import { trackEvent, useUnAuthorize } from '@/hooks';
import { BeaconEventType } from '@/common/enum';
import GeoFenceLocationsModal from '../components/GeoFenceLocationsModal';
import ShipmentNotificationHeader from './components/ShipmentNotificationHeader/ShipmentNotificationHeader';

const GEO_FENCE_ALERT_TYPE = 'GEO_FENCE';
const DEFAULT_GEO_FENCE_CENTER = { lat: 37.7749, lng: -122.4194 };

const getGeoFenceDetailFromNotification = (item) => {
  if (item?.geo_fence && typeof item.geo_fence === 'object') {
    return item.geo_fence;
  }

  return null;
};

const getGeoFenceLocationFromNotification = (item) => {
  const detail = getGeoFenceDetailFromNotification(item);
  if (!detail?.stop_key) return null;

  return {
    id: detail.stop_key,
    stopKey: detail.stop_key,
    menuLabel: detail.stop_label,
    name: detail.stop_label,
    address: detail.stop_address,
    coordinates: detail.latlng ?? DEFAULT_GEO_FENCE_CENTER,
    locationId: detail.location_id ?? null,
    executionLegId: detail.execution_leg_id ?? null,
    hasDefaultFence: false,
    hasPerShipmentFence: true,
  };
};

const ShipmentNotificationDrawer = ({
  allPermissions,
  toggleDrawer,
  shipmentId,
  highlightNotificationId = undefined,
  onShipmentNotificationActivityChange = undefined,
}) => {
  const { token, checkTokenExpiry } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const [selected, setSelected] = useState(null);
  const [muteNotification, setMuteNotification] = useState(false);
  const { unauthorize } = useUnAuthorize();

  //new code
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [notificationModalType, setNotificationModalType] = useState(null);
  const [_hasNotification, setHasNotification] = useState(false);
  const [geoFenceModalOpen, setGeoFenceModalOpen] = useState(false);
  const [geoFenceLocation, setGeoFenceLocation] = useState(null);

  const {
    data: apiData,
    isLoading,
    refetch: fetchNotifications,
  } = useQuery({
    queryKey: ['shipment-notifications', shipmentId],
    queryFn: async () => {
      const res = await shipmentNotificationListing(token, shipmentId);
      return res?.data?.data?.data ?? {};
    },
    enabled: Boolean(shipmentId && token),
  });

  const notificationsData = {
    companyLevel: apiData?.company_level || [],
    shipmentSpecific: apiData?.shipment_specific || [],
  };

  useEffect(() => {
    if (isLoading) {
      showSpin();
    } else {
      hideSpin();
    }
  }, [isLoading, showSpin, hideSpin]);

  // Track opening this shipment's notification-rules drawer. No single
  // notification is "viewed" here (it's a list of rules), so only
  // shipment_id is sent, no reference_id.
  useEffect(() => {
    if (!shipmentId) return;
    trackEvent(BeaconEventType.NotificationViewed, { shipment_id: shipmentId });
  }, [shipmentId]);

  useEffect(() => {
    if (apiData) {
      const muteStatus = apiData?.mute_status ?? false;
      setMuteNotification(muteStatus);
      const companyLevel = apiData?.company_level || [];
      const shipmentSpecific = apiData?.shipment_specific || [];
      const hasNotifs = companyLevel.length > 0 || shipmentSpecific.length > 0;
      setHasNotification(hasNotifs);
      onShipmentNotificationActivityChange?.(
        shipmentId,
        hasNotifs ? (muteStatus ? 'muted' : 'on') : null
      );
    }
  }, [apiData, onShipmentNotificationActivityChange, shipmentId]);

  const setShipmentMute = useCallback(
    (muteForYourself, onSuccess, showMessage = true) => {
      const authToken = checkTokenExpiry();
      muteShipmentNotificationsYourself(authToken, {
        shipment: shipmentId,
        mute_for_yourself: muteForYourself,
      })
        .then((res) => {
          const data = res?.data || {};
          if (data?.data?.is_success) {
            const nextMuteStatus = data?.data?.data?.mute_status ?? false;
            setMuteNotification(nextMuteStatus);
            if (showMessage) {
              message.success(data?.data?.message);
            }
            onShipmentNotificationActivityChange?.(
              shipmentId,
              nextMuteStatus ? 'muted' : 'on'
            );
            onSuccess?.();
          } else {
            message.error(data?.data?.message);
          }
        })
        .catch((error) => {
          unauthorize(error);
        });
    },
    [
      checkTokenExpiry,
      onShipmentNotificationActivityChange,
      shipmentId,
      unauthorize,
    ]
  );

  const handleNotificationCreated = () => {
    if (muteNotification) {
      setShipmentMute(false, () => {}, false);
    }
  };

  const handleCreate = (type) => {
    setOpenNotificationModal(true);
    setNotificationModalType(type);
    setSelected(null);
  };

  const handleGeoFenceEdit = (item) => {
    const detail = getGeoFenceDetailFromNotification(item);
    const location = getGeoFenceLocationFromNotification(item);

    if (!detail || !location) {
      message.error('Unable to edit this geo-fence');
      return;
    }

    setGeoFenceLocation(location);
    setGeoFenceModalOpen(true);
  };

  const handleDeleteSuccess = (res) => {
    const data = res?.data;
    if (data?.data?.is_success) {
      message.success(data?.data?.message);
      fetchNotifications();
    }
  };

  const handleNotificationDelete = async (item) => {
    if (item?.alert_type === GEO_FENCE_ALERT_TYPE) {
      const detail = getGeoFenceDetailFromNotification(item);
      if (!detail?.stop_key) {
        message.error('Unable to delete this geo-fence');
        return;
      }

      const res = await DeleteShipmentGeoFence(
        token,
        shipmentId,
        detail.stop_key
      );
      handleDeleteSuccess(res);
      return;
    }

    const res = await deleteShipmentNotification(item?.id, token);
    handleDeleteSuccess(res);
  };

  const pageTabs = [
    {
      key: '1',
      label: (
        <Tooltip
          title='Previously created notifications in Settings page, that apply to this shipment'
          placement='topLeft'
        >
          <Flex align='center' gap={8}>
            <>
              <CustomShipmentText
                text={'Company-level'}
                fontSize='14px'
                lineHeight='22px'
                color={'#5e5e5e'}
                className='shipment-tab-label'
              />
              <Tag className='shipment-notifications-count-badge'>
                {notificationsData?.companyLevel?.length || 0}
              </Tag>
            </>
          </Flex>
        </Tooltip>
      ),
      children: (
        <>
          <NotificationListSection
            list={notificationsData.companyLevel}
            isCompanyLevel
            onCreate={handleCreate}
          />
        </>
      ),
    },
    {
      key: '2',
      label: (
        <Flex align='center' gap={8}>
          <>
            <CustomShipmentText
              text={'Shipment-specific'}
              fontSize='14px'
              lineHeight='22px'
              color={'#5e5e5e'}
              className='shipment-tab-label'
            />
            <Tag className='shipment-notifications-count-badge'>
              {notificationsData?.shipmentSpecific?.length || 0}
            </Tag>
          </>
        </Flex>
      ),
      children: (
        <>
          <NotificationListSection
            list={notificationsData?.shipmentSpecific}
            isCompanyLevel={false}
            highlightId={highlightNotificationId}
            onCreate={handleCreate}
            onEdit={(item) => {
              if (item?.alert_type === 'ONE_TIME') {
                message.error("You can't edit one-time notification");
                return;
              }
              if (item?.alert_type === GEO_FENCE_ALERT_TYPE) {
                handleGeoFenceEdit(item);
                return;
              }
              const selectedCopy = { ...item };
              setSelected(null);
              setSelected(selectedCopy);
              setNotificationModalType(1);
              setOpenNotificationModal(true);
            }}
            onDelete={handleNotificationDelete}
          />
        </>
      ),
    },
  ];

  return (
    <Flex vertical className='h-full' gap={24}>
      <ShipmentNotificationHeader
        toggleDrawer={toggleDrawer}
        shipmentId={shipmentId}
        muteNotification={muteNotification}
        checkTokenExpiry={checkTokenExpiry}
        unauthorize={unauthorize}
        setMuteNotification={setMuteNotification}
        onMuteToggle={setShipmentMute}
      />
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          marginBottom: '24px',
        }}
      >
        <CustomTabs
          items={pageTabs}
          className='shipment-notification-tabs'
          defaultActiveKey={highlightNotificationId ? '2' : undefined}
        />
      </div>
      {allPermissions?.is_create && (
        <div
          style={{
            background: 'var(--surface-container)',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '100%',
            padding: '0px 24px 24px',
          }}
        >
          <Row gutter={[0, 8]}>
            <CustomButton
              text='Set up shipment notification'
              bgColor='#000'
              color='#fff'
              fontSize='16px'
              fontWeight='600'
              lineHeight={'20px'}
              height='48px'
              width='100%'
              borderRadius='8px'
              boxShadow='none'
              onClick={() => handleCreate(1)}
            />
          </Row>
        </div>
      )}
      {openNotificationModal && (
        <PerShipmentNotificationModal
          selected={selected}
          fetchNotifications={fetchNotifications}
          shipmentId={shipmentId}
          notificationModalType={notificationModalType}
          openNotificationModal={openNotificationModal}
          setOpenNotificationModal={setOpenNotificationModal}
          isMultiple={false}
          onNotificationCreated={handleNotificationCreated}
        />
      )}
      {geoFenceModalOpen && (
        <GeoFenceLocationsModal
          open={geoFenceModalOpen}
          shipmentId={shipmentId}
          location={geoFenceLocation}
          onClose={() => {
            setGeoFenceModalOpen(false);
            setGeoFenceLocation(null);
          }}
          onApply={() => {
            setGeoFenceModalOpen(false);
            setGeoFenceLocation(null);
            fetchNotifications();
          }}
          onRemoved={() => {
            setGeoFenceModalOpen(false);
            setGeoFenceLocation(null);
            fetchNotifications();
          }}
        />
      )}
    </Flex>
  );
};

export default ShipmentNotificationDrawer;
